import { Sparkles, CheckCircle2, Clock, Radar } from 'lucide-react'
import type { SavedSearch } from '@/lib/types'

// Color rotation for each Roostr slot
export const ROOSTR_COLORS = [
  { bg: 'bg-primary/15', border: 'border-primary/40', text: 'text-primary', glow: 'ring-primary/20', pillBg: 'bg-primary/10', left: 'border-l-primary' },
  { bg: 'bg-violet-500/15', border: 'border-violet-500/40', text: 'text-violet-500', glow: 'ring-violet-500/20', pillBg: 'bg-violet-500/10', left: 'border-l-violet-500' },
  { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-500', glow: 'ring-emerald-500/20', pillBg: 'bg-emerald-500/10', left: 'border-l-emerald-500' },
  { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-500', glow: 'ring-amber-500/20', pillBg: 'bg-amber-500/10', left: 'border-l-amber-500' },
  { bg: 'bg-rose-500/15', border: 'border-rose-500/40', text: 'text-rose-500', glow: 'ring-rose-500/20', pillBg: 'bg-rose-500/10', left: 'border-l-rose-500' },
]

// Get the default name for a given slot (0-indexed)
export function getDefaultRoostrName(slotIndex: number) {
  return `Roostr ${slotIndex + 1}`
}

// Get display name: custom name if user set one, otherwise slot-based default
export function getRoostrDisplayName(search: SavedSearch, slotIndex: number) {
  return search.roostr_name || getDefaultRoostrName(slotIndex)
}

// Mood system with icons and pill colors
export function getRoostrMood(search: SavedSearch): {
  label: string
  icon: React.ElementType
  pillBg: string
  pillText: string
  dotColor: string
} {
  if (!search.last_checked_at) {
    return {
      label: 'Just deployed',
      icon: Sparkles,
      pillBg: 'bg-violet-500/10',
      pillText: 'text-violet-600 dark:text-violet-400',
      dotColor: 'bg-violet-500',
    }
  }

  const lastChecked = new Date(search.last_checked_at)
  const hoursSinceCheck = (Date.now() - lastChecked.getTime()) / (1000 * 60 * 60)

  if (search.notification_count && search.notification_count > 0 && hoursSinceCheck < 48) {
    return {
      label: 'Found results',
      icon: CheckCircle2,
      pillBg: 'bg-emerald-500/10',
      pillText: 'text-emerald-600 dark:text-emerald-400',
      dotColor: 'bg-emerald-500',
    }
  }

  if (hoursSinceCheck > 168) { // > 1 week
    return {
      label: 'Searching...',
      icon: Clock,
      pillBg: 'bg-amber-500/10',
      pillText: 'text-amber-600 dark:text-amber-400',
      dotColor: 'bg-amber-500',
    }
  }

  return {
    label: 'On patrol',
    icon: Radar,
    pillBg: 'bg-primary/10',
    pillText: 'text-primary',
    dotColor: 'bg-primary',
  }
}

// Build a short summary of a saved search's criteria
export function getSearchSummary(search: SavedSearch): string {
  const parts: string[] = []

  if (search.original_query) {
    const q = search.original_query
    parts.push(`"${q.length > 30 ? q.substring(0, 30) + '...' : q}"`)
  }

  if (search.parsed_filters) {
    const filterCount = Object.keys(search.parsed_filters).filter(k => k !== 'country').length
    if (filterCount > 0) {
      parts.push(`${filterCount} filter${filterCount !== 1 ? 's' : ''}`)
    }
  }

  if (search.keywords && search.keywords.length > 0) {
    parts.push(`${search.keywords.length} keyword${search.keywords.length !== 1 ? 's' : ''}`)
  }

  if (search.travel_time) {
    parts.push(`${search.travel_time.minutes}min ${search.travel_time.mode}`)
  }

  if (search.has_semantic_search) {
    parts.push('semantic')
  }

  return parts.length > 0 ? parts.join(' · ') : 'No criteria'
}
